import { ErrorBoundary } from '@sentry/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';
import { Suspense, useMemo } from 'react';

import { signalListQueryOptions } from '@/entities/signal';
import { CreateSignalDialog } from '@/features/(signal)/signal-create-dialog';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import { Skeleton } from '@/shared/ui';

import {
  type CompositedSignalIssue,
  MAX_DISPLAY_ISSUE_COUNT,
} from '../../model';
import SignalAddButton from './signal-add-button';
import SignalButton from './signal-button';

type Props = {
  issues: CompositedSignalIssue[];
};
export function SignalList({ issues }: Props) {
  const workspaceId = useWorkspaceId();
  const handleAddSignal = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <CreateSignalDialog
        workspaceId={workspaceId}
        isOpen={isOpen}
        close={close}
        unmount={unmount}
      />
    ));
  };

  return (
    <div className="flex gap-2 overflow-y-auto flex-1">
      <SignalAddButton onClick={handleAddSignal} />
      <AllSignalButton issues={issues} />
      <ErrorBoundary fallback={<></>}>
        <Suspense
          fallback={
            <div className="flex items-center gap-2">
              <Skeleton className="w-30 h-full" />
              <Skeleton className="w-40 h-full" />
            </div>
          }
        >
          <DefinedSignalButtons issues={issues} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

const AllSignalButton = ({ issues }: Props) => {
  return (
    <SignalButton
      id="all"
      title="All issues"
      count={Math.min(issues.length, MAX_DISPLAY_ISSUE_COUNT)}
      hiddenMoreAction
    />
  );
};

const DefinedSignalButtons = ({ issues }: Props) => {
  const workspaceId = useWorkspaceId();
  const { data } = useSuspenseQuery(signalListQueryOptions({ workspaceId }));

  // signal별 issue 수 계산
  const countBySignalId = useMemo(() => {
    const map = new Map<string, number>();
    for (const issue of issues) {
      for (const signalId of issue.signalIds) {
        map.set(signalId, (map.get(signalId) ?? 0) + 1);
      }
    }
    return map;
  }, [issues]);

  return (
    <>
      {data.map((v) => (
        <SignalButton
          key={v.id}
          id={v.id}
          title={v.title}
          definition={v.definition}
          count={countBySignalId.get(v.id) ?? 0}
        />
      ))}
    </>
  );
};
